<style lang="scss">
@import "resources/sass/v1/abstracts/_variables";

.advanced-hero {
    position: relative;
    margin-bottom: -80px;

    @media screen and (min-width: $screen-md) {
        margin-bottom: 0;
    }

    .hero {
        background-color: $primary;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        padding: 40px 0 120px 0;
        position: relative;
        margin-bottom: 250px;

        @media screen and (min-width: $screen-lg) {
            padding: 80px 0 0 0;
        }

        @media screen and (min-width: $screen-xl) {
            padding: 158px 0 0 0;
        }

        &:before {
            position: absolute;
            z-index: 1;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            content: "";
            background-color: rgba(43, 74, 132, 0.7);
        }

        .content {
            position: relative;
            z-index: 2;
        }
    }

    &:after {
        content: "";
        background-image: url("/svg/shapes/header-1.svg");
        height: 80px;
        width: 100%;
        position: absolute;
        z-index: 1;
        right: 0;
        bottom: 0;

        @media screen and (min-width: $screen-sm) {
            background-size: cover;
            background-repeat: no-repeat;
        }
    }

    .cards-row {
        margin-bottom: -160px;

        @media screen and (min-width: $screen-md) {
            flex-wrap: wrap;
            margin-bottom: -260px;
        }

        @media screen and (min-width: $screen-lg) {
            margin-bottom: -144px;
            z-index: 5;
            width: 100%;
        }
    }

    .card-box {
        position: relative;
        z-index: 50;
        width: 100%;
        box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.03);
        border-radius: 4px;
        background-color: #ffffff;

        &:last-child {
            margin-right: 0 !important;
        }

        @media screen and (min-width: $screen-md) {
            width: calc(50% - 12px);
            min-height: 256px;
            margin-right: 24px;

            &:nth-child(2n) {
                margin-right: 0;
            }
        }

        @media screen and (min-width: $screen-lg) {
            width: calc(100 / 3 * 1% - 16px);
            min-height: 312px;

            &:nth-child(2n) {
                margin-right: 24px;
            }
        }

        &.more-than-three {
            @media screen and (min-width: $screen-lg) {
                width: calc(50% - 12px);
                min-height: 216px;

                &:nth-child(2n) {
                    margin-right: 0;
                }
            }

            @media screen and (min-width: $screen-xl) {
                min-height: 216px;
            }

            @media screen and (min-width: 1920px) {
                min-height: 216px;
            }
        }
    }

    h2 {
        @media screen and (max-width: $screen-lg-end) {
            font-size: 1.75rem;
            line-height: 1.5;
        }
    }

    .box-text {
        color: #484848;
        font-size: 1rem;
        font-weight: $font-weight-light;
        letter-spacing: 0.2px;
        line-height: 28px;
    }

    .read-more {
        @media screen and (min-width: $screen-md) {
            position: absolute;
            bottom: 0;
            left: 0;
        }
    }

    .navbar-extra {
        position: fixed;
        width: 100%;
        max-width: 1920px;
        left: 50%;
        transform: translateX(-50%);
        //top: 0;
        bottom: calc(100% - 64px);
        transition: transform 0.2s, box-shadow 0.15s,
            border-top 0.15s linear 0.15s;
        min-height: 40px;
        z-index: 9999;
        border-bottom: 1px solid rgba(176, 177, 185, 0.5);
        border-top: 1px solid #fff;

        &.visible {
            transform: translateY(100%) translateX(-50%);
            border-top: 1px solid rgba(176, 177, 185, 0.5);
        }

        .menu-item {
            a {
                color: #484848;
                font-size: 14px;
                font-weight: $font-weight-bold;
            }
            &.active {
                border-bottom: 2px solid $secondary;
                a {
                    color: $secondary;
                }
            }
        }

        @media screen and (min-width: $screen-md) {
            height: 56px;
        }

        @media screen and (min-width: $screen-xl) {
            bottom: calc(100% - 80px);
        }
    }
}

header.fixed + .base {
    .navbar-extra {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
    }
}
</style>

<template>
    <div class="advanced-hero">
        <div class="navbar-extra bg-white d-flex flex-wrap justify-content-center " :class="{'visible': extraNavIsVisible}">
            <div v-for="(section, index) in sections" :key="index" class="mx-16 menu-item d-flex flex-column justify-content-center" :class="{ 'active': `#${slug(section.title_detail)}` ===  activeHash}" :href="`#${slug(section.title)}`">
                <a class="my-8" @click.prevent="scroll" :href="`#${slug(section.title_detail)}`">{{ section.title }}</a>
            </div>
        </div>

        <div class="hero" :style="possibleBackgroundImage">
            <div class="container content">
                <div class="row mb-40 mb-md-80">
                    <div class="col-12 col-xl-10">
                        <div class="intro d-lg-flex">
                            <h1 class="h2 text-white word-break">{{ title }}</h1>
                            <div class="text-white font-weight-light text-cut ml-lg-80 mb-child-0">
                                <p v-if="description" v-html="description"></p>
                                <slot></slot>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 position-relative">
                        <div class="cards-row d-flex flex-column flex-md-row">
                            <div v-for="(section, index) in sections" class="card-box mb-24" :class="{'more-than-three': sections.length > 3}" :key="index">
                                <a class="d-inline-block p-32 p-xl-40 posi w-100 h-100" @click.prevent="scroll" :href="`#${slug(section.title_detail)}`">
                                    <div class="position-relative w-100 h-100">
                                        <h2 class="mb-16 mb-xl-24">{{ section.title }}</h2>
                                        <p v-if="section.description" class="box-text mb-32">{{ section.description }}</p>
                                        <span class="read-more">{{ translations.read_more }}</span>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
var scrollIntoView = require('scroll-into-view');
var slugify = require('slugify')

export default {
    props: {
        title: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            required: false,
        },
        image: {
            type: String,
            required: false,
        },
        sections: {
            type: Array,
            required: false
        },
        translations: {
            type: Object,
            required: true
        },
    },

    data() {
        return {
            extraNavVisible: false,
            activeHash: null,
        }
    },

    mounted() {
        let self = this;
        let last_known_scroll_position = 0;
        let ticking = false;

        this.setHash(location.hash)

        window.addEventListener('scroll', function (e) {
            last_known_scroll_position = window.pageYOffset;

            if (!ticking) {
                window.requestAnimationFrame(function () {
                    if (last_known_scroll_position > 720) {
                        self.extraNavVisible = true;
                    } else {
                        self.extraNavVisible = false;
                    }

                    ticking = false;
                });

                ticking = true;
            }
        });

    },

    computed: {
        possibleBackgroundImage() {
            if (this.image) return `background-image: url('${this.image}')`;
            return '';
        },

        extraNavIsVisible() {
            return this.extraNavVisible;
        },
    },

    methods: {
        scroll(e) {
            this.setHash(e.target.hash)
            e.preventDefault();
            if (e.target.getAttribute('href') == null) {
                scrollIntoView(document.querySelector(e.target.closest("a").getAttribute('href')));
            } else {
                scrollIntoView(document.querySelector(e.target.getAttribute('href')));
            }
        },

        slug(string) {
            return slugify(string, { lower: true });
        },

        setHash(hash) {
            this.activeHash = hash
        }
    }
}
</script>
